import { Icon } from "@fluentui/react";
import { Form, Input, Upload, UploadProps, notification, message, UploadFile, Select, Spin, Checkbox } from "antd";
import { FileIconType, getFileTypeIconProps } from "@fluentui/react-file-type-icons";
import Modal from "antd/es/modal/Modal";
import { InboxOutlined } from "@ant-design/icons";
import "./FileUploadModal.css";
import TextArea from "antd/es/input/TextArea";
import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { getExtension } from "../../../../utils";
import { useDispatch, useSelector } from "react-redux";
import { useAppDispatch } from "../../../../redux/useAppDispatch";
import { documentUpload, fetchTargetLanguageList, fetchUploadMetaData, resetTabState } from "../../../../redux/EngageTranslation/EngageTranslationAction";
import { RootState } from "../../../../redux/Store";
import { openNotification } from "../../../Template/NotificationComponent";

type FileUploadModalProps = {
  close: () => void;
  openState: boolean;
  open: () => void;
};
const formItemLayout = {
  labelCol: {
    xs: { span: 24 },
  },
  wrapperCol: {
    xs: { span: 24 },
  },
};

const acceptFormat = ["application/pdf", "application/msword", "application/vnd.openxmlformats-officedocument.wordprocessingml.document"];

function arePropsEqual(oldProps: FileUploadModalProps, newProps: FileUploadModalProps) {
  return oldProps.openState === newProps.openState;
}
function FileUploadModal(props: FileUploadModalProps) {
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [form] = Form.useForm();
  const clearUploadForm = () => {
    form.resetFields();
    setFileList([]);
  };
  const dispatch = useAppDispatch();
  const uploadFormState = useSelector((state: RootState) => state.engageTranslationState.engageTranslateMainState.uploadFormMetaDataState);

  useEffect(() => {
    if (props.openState) {
      form.setFieldValue("isScanned", false);
      dispatch(fetchUploadMetaData());
    } else {
      clearUploadForm();
    }
  }, [props.openState]);

  const arrangeLanguagesAlphabetically = (languagesArray: Array<{ label: string; value: number; id: number }>) => {
    return [...languagesArray].sort((a: any, b: any) => a.label.localeCompare(b.label, "en", { sensitivity: "base" }));
  };

  const FileUploadModalContent = memo(() => {
    const { Dragger } = Upload;
    function handleSourceLanguageChange(value: number) {
      dispatch(fetchTargetLanguageList(value));
      form.setFieldsValue({ targetLanguageIds: null });
    }
    const uploadProps: UploadProps = {
      name: "file",
      multiple: false,
      // disabled: true,
      showUploadList: false,

      beforeUpload: (file) => {
        const isPDF = acceptFormat.includes(file.type);

        if (!isPDF) {
          return false;
        }
        setFileList((prev) => [file]);

        return false;
      },
    };

    const handleSubmission = async (values: any) => {
      dispatch(resetTabState("new"));
      dispatch(documentUpload(values));
      props.close();
    };

    const preventMinus = (e: any) => {
      if (e.code === "Minus") {
        e.preventDefault();
      }
    };

    return (
      <Spin spinning={uploadFormState.loading}>
        <div className="d-flex flex-column  justify-content-between gap-4 h-100 needs-validation">
          <h4 className="m-0 p-0">Upload Document</h4>

          <Form
            className="d-flex gap-5"
            form={form}
            onFinish={(values) => {
              handleSubmission(values);
            }}
            // disabled={false}
            scrollToFirstError
            {...formItemLayout}
            colon={false}>
            <div>
              <Form.Item
                name="name"
                required
                rules={[{ required: true, message: "Please provide Document Name." }]}
                label="Document">
                <Input />
              </Form.Item>
              <Form.Item
                name={"description"}
                label="Description">
                <TextArea
                  rows={4}
                  placeholder="Description"
                  showCount
                  maxLength={100}
                />
              </Form.Item>
              <Form.Item
                name="clientId"
                required
                rules={[{ required: true, message: "Please provide Client Name." }]}
                label="Client Name">
                <Select options={uploadFormState.clientList} />
              </Form.Item>
              <Form.Item
                name={"engageProductionId"}
                required
                rules={[
                  {
                    validator: (rule, value, callback) => {
                      // Custom validation logic
                      //convert 00 to 0 000 to 0 0000 to 0
                      //validate if the value is smaller than 0

                      if (Number(value) <= 0) {
                        callback("Please provide valid ID");
                      } else {
                        callback();
                      }
                    },
                  },
                  // { pattern: new RegExp(/^[0-9]+$/), message: "Please provide valid ID" },
                  // { required: true, message: "Please provide valid ID" },
                ]}
                label="Engage ID">
                <Input
                  type="number"
                  min={0}
                  onKeyDown={preventMinus}
                />
              </Form.Item>
              <Form.Item
                name={"sourceLanguageId"}
                required
                rules={[
                  {
                    required: true,
                    message: "Please provide Source Language.",
                  },
                ]}
                label="Source Language">
                <Select
                  onChange={(value) => {
                    handleSourceLanguageChange(value);
                  }}
                  options={arrangeLanguagesAlphabetically(uploadFormState.sourceLanguageList)}
                />
              </Form.Item>
              <Form.Item
                name={"targetLanguageIds"}
                required
                rules={[
                  {
                    required: true,
                    message: "Please provide Output Language.",
                  },
                ]}
                label="Output Language">
                <Select
                  mode="tags"
                  options={arrangeLanguagesAlphabetically(uploadFormState.targetLanguageList)}
                />
              </Form.Item>
              <Form.Item
                required
                name={"uploadedBy"}
                rules={[
                  { type: "email", message: "Please provide valid email" },
                  {
                    required: true,
                    message: "Please provide Workflow Coordinator.",
                  },
                ]}
                label="Workflow Coordinator">
                <Input />
              </Form.Item>
              {/* <Form.Item
                required
                name={"qcAssignedTo"}
                label="Assigned to (For QC)"
                rules={[
                  { type: "email", message: "Please provide valid email" },
                  {
                    required: true,
                    message: "Please provide Allocate to (For QC)",
                  },
                ]}>
                <Input />
              </Form.Item> */}
              <Form.Item
                required
                valuePropName="checked"
                name={"isScanned"}>
                <Checkbox defaultChecked={false}>Scanned</Checkbox>
              </Form.Item>
            </div>
            <div className="full-flex d-flex flex-column">
              <Form.Item
                name={"file"}
                wrapperCol={{ flex: 1 }}
                getValueFromEvent={(e) => {
                  return e.fileList[0];
                }}
                style={{ height: "100%" }}
                rules={[
                  {
                    validator: (rule, value, callback) => {
                      // Custom validation logic
                      if (value.size / 1024 / 1024 > 2000) {
                        callback("File size should be less than 2 GB");
                        // } else if (fileUploadArray[value.name] !== undefined) {
                        //   callback("File already exists");
                      } else if (!acceptFormat.includes(value.type)) {
                        callback("Please upload a PDF or DOCX document");
                      } else if (rule.required) {
                        callback("Please upload a document");
                      } else {
                        callback();
                      }
                    },
                  },
                ]}>
                <Dragger
                  height={750}
                  accept={".pdf, .doc, .docx"}
                  {...uploadProps}>
                  {fileList.length > 0 ? (
                    <div className="d-flex flex-column align-items-center">
                      <Icon
                        {...getFileTypeIconProps({
                          extension: getExtension(fileList[0].type),
                          size: 96,
                          imageFileType: "svg",
                        })}
                      />
                      {fileList[0].name}
                    </div>
                  ) : (
                    <div>
                      <p className="ant-upload-drag-icon">
                        <InboxOutlined />
                      </p>
                      <p className="ant-upload-text">Click or drag a file to this area to upload</p>
                      <p className="ant-upload-hint">Support for a single upload</p>
                    </div>
                  )}
                </Dragger>
              </Form.Item>
            </div>
          </Form>

          <div className="d-flex gap-4 justify-content-between">
            <button
              className="button warning"
              onClick={() => clearUploadForm()}>
              Clear
            </button>
            <div className="d-flex gap-4">
              <button
                type="submit"
                onClick={() => {
                  form.submit();
                }}
                className="button">
                Ok
              </button>
              <button
                type="button"
                onClick={() => props.close()}
                className="button warning">
                Cancel
              </button>
            </div>
          </div>
        </div>
      </Spin>
    );
  });
  return (
    <Modal
      centered
      width={1000}
      zIndex={1500}
      footer={[]}
      onCancel={() => props.close()}
      open={props.openState}>
      <FileUploadModalContent />
    </Modal>
  );
}

export default memo(FileUploadModal, arePropsEqual);
